import { useEffect, useState, useCallback } from "react";
import {
  Bell, RefreshCw, AlertTriangle, Info, CheckCircle2, AlertCircle,
  Clock, Send, Mail, Smartphone, ChevronDown, User, Inbox, X, Paperclip, Link2
} from "lucide-react";
import { dashboardService, type DashboardActivity } from "@/services/dashboardService";
import { dashboardService as ds } from "@/services/dashboardService";
import { notificationService, type SentNotification, type RecipientType } from "@/services/notificationService";
import { useTeacherTheme } from "@/contexts/TeacherThemeContext";

// ── helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  new: "bg-red-100 text-red-700 border-red-200",
  acknowledged: "bg-purple-100 text-purple-700 border-purple-200",
  investigating: "bg-yellow-100 text-yellow-700 border-yellow-200",
  resolved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  escalated: "bg-red-200 text-red-800 border-red-300",
  dismissed: "bg-gray-100 text-gray-600 border-gray-200",
};

function timeAgo(iso: string): string {
  try {
    const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
    if (s < 60) return 'Just now';
    const m = Math.floor(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

// ── Inbox tab ─────────────────────────────────────────────────────────────────

function InboxTab({ dm }: { dm: boolean }) {
  const [notifications, setNotifications] = useState<DashboardActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [activeSection, setActiveSection] = useState<'urgent' | 'recent'>('urgent');

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await dashboardService.getDashboardActivity(page, 20);
      setNotifications(response.data);
      setTotalPages(response.totalPages);
      setLastUpdated(new Date());
    } catch { /* silent */ } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refresh when a new safety alert push fires
  useEffect(() => {
    const handler = () => fetchNotifications();
    window.addEventListener('new-safety-alert', handler);
    return () => window.removeEventListener('new-safety-alert', handler);
  }, [fetchNotifications]);

  // Requires attention: any new/escalated alert from system, AI, or admin
  const isUrgent = (n: DashboardActivity) =>
    n.status === 'new' || n.status === 'escalated';

  const grouped = {
    urgent: notifications.filter(isUrgent),
    other: notifications.filter(n => !isUrgent(n)),
  };

  function iconBg(type: string, status?: string) {
    if (status === 'resolved') return 'bg-emerald-50';
    if (status === 'escalated') return 'bg-red-100';
    if (status === 'new' && type === 'incident') return 'bg-red-50';
    if (type === 'system') return 'bg-blue-50';
    return 'bg-slate-100';
  }

  function NotifIcon({ type, status }: { type: string; status?: string }) {
    if (status === 'resolved') return <CheckCircle2 className="h-4 w-4 text-emerald-500" />;
    if (status === 'escalated' || type === 'incident') return <AlertTriangle className="h-4 w-4 text-red-500" />;
    if (type === 'system') return <Info className="h-4 w-4 text-blue-500" />;
    return <Bell className="h-4 w-4 text-slate-400" />;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
          {lastUpdated
            ? `Updated ${lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · auto-refreshes every 60s`
            : 'Loading…'}
        </p>
        <button
          onClick={fetchNotifications}
          disabled={loading}
          className="btn-glass btn-glass-pill flex items-center gap-1.5"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading && notifications.length === 0 ? (
        <div className="flex justify-center items-center py-20 text-slate-400">
          <RefreshCw className="animate-spin h-5 w-5 mr-2" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center py-20 text-slate-400 gap-3">
          <Bell className="h-10 w-10 text-slate-300" />
          <p className="text-sm">No notifications yet.</p>
        </div>
      ) : (
        <>
          {/* Tab toggle */}
          <div className="flex gap-1" data-dm={dm}>
            {([
              { key: 'urgent' as const, label: 'Requires Attention', icon: AlertCircle, count: grouped.urgent.length },
              { key: 'recent' as const, label: 'Recent Activity', icon: Info, count: grouped.other.length },
            ]).map(({ key, label, icon: Icon, count }) => {
              const isActive = activeSection === key;
              const activeCls = isActive ? (key === 'urgent' ? 'btn-glass-danger' : 'btn-glass-blue') : '';
              return (
                <button
                  key={key}
                  onClick={() => setActiveSection(key)}
                  className={`btn-glass btn-glass-pill flex items-center gap-2 ${activeCls}`}
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  {label}
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold tabular-nums ${isActive ? 'bg-white/20 text-white' : (dm ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600')}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Content */}
          <div className="space-y-2">
            {activeSection === 'urgent' ? (
              grouped.urgent.length === 0 ? (
                <div className={`flex items-center gap-2 px-4 py-3 rounded-xl border text-sm ${dm ? 'border-slate-700 bg-slate-800 text-slate-500' : 'border-slate-100 bg-slate-50 text-slate-400'}`}>
                  <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                  All clear — no alerts require attention
                </div>
              ) : grouped.urgent.map(n => (
                <NotifCard key={n.id} n={n} iconBg={iconBg} NotifIcon={NotifIcon} dm={dm} />
              ))
            ) : (
              grouped.other.length === 0 ? (
                <div className={`px-4 py-3 rounded-xl border text-sm ${dm ? 'border-slate-700 bg-slate-800 text-slate-500' : 'border-slate-100 bg-slate-50 text-slate-400'}`}>
                  No recent activity
                </div>
              ) : grouped.other.map(n => (
                <NotifCard key={n.id} n={n} iconBg={iconBg} NotifIcon={NotifIcon} dm={dm} />
              ))
            )}
          </div>

          {totalPages > 1 && (
            <div className={`flex items-center justify-between border rounded-xl px-4 py-3 shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <p className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Page {page} of {totalPages}</p>
              <div className="flex gap-2">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1 || loading} className="btn-glass btn-glass-pill">Previous</button>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} className="btn-glass btn-glass-pill">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NotifCard({
  n,
  iconBg,
  NotifIcon,
  dm,
}: {
  n: DashboardActivity;
  iconBg: (type: string, status?: string) => string;
  NotifIcon: React.FC<{ type: string; status?: string }>;
  dm: boolean;
}) {
  const urgent = n.type === 'incident' && (n.status === 'new' || n.status === 'escalated');
  return (
    <div className={`border rounded-xl p-4 shadow-sm transition hover:shadow-md ${urgent ? (dm ? 'border-red-700 ring-1 ring-red-900' : 'border-red-200 ring-1 ring-red-100') : (dm ? 'border-slate-700' : 'border-slate-200')} ${dm ? 'bg-slate-800' : 'bg-white'}`}>
      <div className="flex gap-3">
        <div className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full ${iconBg(n.type, n.status)}`}>
          <NotifIcon type={n.type} status={n.status} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className={`text-sm font-semibold leading-snug ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{n.message}</p>
            {n.status && (
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium border capitalize ${STATUS_COLORS[n.status] || 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                {n.status}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <Clock className="h-3 w-3" />
            {timeAgo(String(n.timestamp))}
            <span className="text-slate-200">·</span>
            <span className="capitalize text-slate-400">{n.type === 'incident' ? 'Safety alert' : n.type}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Compose tab ───────────────────────────────────────────────────────────────

interface StudentOption {
  id: string;
  name: string;
  email: string;
}

function ComposeTab({ dm }: { dm: boolean }) {
  const [students, setStudents] = useState<StudentOption[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentSearch, setStudentSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState<StudentOption | null>(null);
  const [recipientType, setRecipientType] = useState<RecipientType>('student_push');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ success: boolean; detail: string } | null>(null);
  const [sentHistory, setSentHistory] = useState<SentNotification[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<SentNotification | null>(null);
  const [attachLink, setAttachLink] = useState('');
  const [attachFile, setAttachFile] = useState<File | null>(null);
  const [attachPreview, setAttachPreview] = useState<string | null>(null);

  useEffect(() => {
    ds.getMonitoredStudents(1, 100)
      .then(res => {
        setStudents(res.data.map(s => ({ id: s.id, name: s.name, email: s.email })));
      })
      .catch(() => {})
      .finally(() => setStudentsLoading(false));

    notificationService.getSent()
      .then(setSentHistory)
      .catch(() => {})
      .finally(() => setHistoryLoading(false));
  }, []);

  const filtered = students.filter(s =>
    s.name.toLowerCase().includes(studentSearch.toLowerCase()) ||
    s.email.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setAttachFile(file);
    if (file) {
      const reader = new FileReader();
      reader.onload = ev => setAttachPreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setAttachPreview(null);
    }
  };

  const handleSend = async () => {
    if (!selectedStudent || !title.trim() || !message.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const fullMessage = attachLink.trim()
        ? `${message.trim()}\n\nAttached link: ${attachLink.trim()}`
        : message.trim();
      const res = await notificationService.send({
        recipientType,
        studentId: selectedStudent.id,
        title: title.trim(),
        message: fullMessage,
      });
      // success=true + delivered=false means in-app fallback — still a success
      setResult({ success: res.success, detail: res.detail });
      if (res.success) {
        // Add to sent history optimistically
        setSentHistory(prev => [{
          id: Date.now().toString(),
          title: title.trim(),
          message: fullMessage,
          sentVia: res.fallback === 'in_app' ? 'in_app' : recipientType === 'student_push' ? 'push' : 'email',
          status: 'read',
          createdAt: new Date().toISOString(),
          recipient: { name: selectedStudent.name, email: selectedStudent.email },
        }, ...prev]);
        setTitle('');
        setMessage('');
        setAttachLink('');
        setAttachFile(null);
        setAttachPreview(null);
        setSelectedStudent(null);
        setStudentSearch('');
      }
    } catch (err: unknown) {
      setResult({ success: false, detail: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setSending(false);
    }
  };

  const canSend = !!selectedStudent && title.trim().length > 0 && message.trim().length > 0 && !sending;

  return (
    <div className="space-y-6">
      {/* Compose form */}
      <div className={`border rounded-xl p-5 shadow-sm space-y-4 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <h3 className={`text-sm font-semibold ${dm ? 'text-slate-200' : 'text-slate-700'}`}>New Notification</h3>

        {/* Recipient type */}
        <div className="flex gap-1" data-dm={dm}>
          {([
            { value: 'student_push', label: 'Push to Student', icon: Smartphone },
            { value: 'parent_email', label: 'Email to Parent', icon: Mail },
          ] as const).map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              onClick={() => setRecipientType(value)}
              className={`btn-glass btn-glass-pill flex items-center gap-2 ${recipientType === value ? 'btn-glass-blue' : ''}`}
            >
              <Icon className="h-4 w-4" />
              {label}
            </button>
          ))}
        </div>
        

        {/* Student picker */}
        <div className="relative">
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            {recipientType === 'student_push' ? 'Student' : 'Student (parent email will be used)'}
          </label>
          <button
            onClick={() => setDropdownOpen(o => !o)}
            className={`w-full flex items-center justify-between px-3 py-2.5 border rounded-lg text-sm text-left transition`}
            style={{ 
              cursor: 'pointer',
              background: dm ? 'rgba(30, 41, 59, 0.7)' : 'rgba(241, 245, 249, 0.9)',
              borderColor: dm ? 'rgba(71, 85, 105, 0.7)' : 'rgba(203, 213, 225, 0.8)',
              color: dm ? '#e2e8f0' : '#1e293b'
            }}
          >
            <span style={{ color: selectedStudent ? '#334155' : '#94a3b8' }}>
              {selectedStudent ? (
                <span className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-slate-400" />
                  {selectedStudent.name}
                </span>
              ) : studentsLoading ? 'Loading students…' : 'Select a student…'}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />
          </button>
          {dropdownOpen && (
            <div className={`absolute z-20 mt-1 w-full border rounded-xl shadow-lg overflow-hidden ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
              <div className={`p-2 border-b ${dm ? 'border-slate-700' : 'border-slate-100'}`}>
                <input
                  autoFocus
                  type="text"
                  placeholder="Search…"
                  value={studentSearch}
                  onChange={e => setStudentSearch(e.target.value)}
                  className={`w-full text-sm px-3 py-1.5 border rounded-lg outline-none focus:ring-1 focus:ring-slate-200 ${dm ? 'bg-slate-700/50 border-slate-600/50 text-slate-200 placeholder-slate-500' : 'bg-white border-slate-200/60 text-slate-600 placeholder-slate-400'}`}
                />
              </div>
              <div className="max-h-48 overflow-y-auto">
                {filtered.length === 0 ? (
                  <p className="px-3 py-3 text-xs text-slate-400">No students found</p>
                ) : (
                  filtered.map(s => (
                    <button
                      key={s.id}
                      onClick={() => { setSelectedStudent(s); setDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-sm transition ${dm ? 'hover:bg-slate-600/50' : 'hover:bg-slate-100/50'}`}
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <p className={`font-medium ${dm ? 'text-slate-200' : 'text-slate-700'}`}>{s.name}</p>
                      <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{s.email}</p>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {/* Title */}
        <div>
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Subject / Title</label>
          <input
            type="text"
            placeholder={recipientType === 'parent_email' ? 'Safety concern regarding your child' : 'Message from your teacher'}
            value={title}
            onChange={e => setTitle(e.target.value)}
            maxLength={120}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 ${dm ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-200 text-slate-800 placeholder-slate-400'}`}
          />
        </div>

        {/* Message */}
        <div>
          <label className={`text-xs font-medium mb-1 block ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Message</label>
          <textarea
            placeholder="Write your message here…"
            value={message}
            onChange={e => setMessage(e.target.value)}
            rows={4}
            maxLength={1000}
            className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-200 resize-none ${dm ? 'bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-500' : 'border-slate-200 text-slate-800 placeholder-slate-400'}`}
          />
          <p className={`text-xs text-right mt-0.5 ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{message.length}/1000</p>
        </div>

        {/* Attachments */}
        <div>
          <label className={`text-xs font-medium mb-2 block ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
            Attachments <span className="font-normal opacity-60">(optional)</span>
          </label>
          <div className="space-y-2">
            {/* Link attachment */}
            <div className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl ${dm ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <Link2 className="h-4 w-4 text-slate-400 shrink-0" />
              <input
                type="url"
                placeholder="Paste a link (e.g. https://example.com)"
                value={attachLink}
                onChange={e => setAttachLink(e.target.value)}
                className={`flex-1 text-sm bg-transparent outline-none ${dm ? 'text-slate-200 placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'}`}
              />
              {attachLink && (
                <button onClick={() => setAttachLink('')} style={{ border: 'none', background: 'none', cursor: 'pointer' }} className={`${dm ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Screenshot attachment */}
            <div className={`flex items-center gap-2 px-3 py-2.5 border rounded-xl ${dm ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}>
              <Paperclip className="h-4 w-4 text-slate-400 shrink-0" />
              <label className="flex-1 cursor-pointer">
                <span className={`text-sm ${attachFile ? (dm ? 'text-slate-200' : 'text-slate-700') : (dm ? 'text-slate-500' : 'text-slate-400')}`}>
                  {attachFile ? attachFile.name : 'Attach a screenshot…'}
                </span>
                <input type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
              </label>
              {attachFile && (
                <button onClick={() => { setAttachFile(null); setAttachPreview(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }} className={`${dm ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600'}`}>
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Screenshot preview */}
            {attachPreview && (
              <img src={attachPreview} alt="Preview" className="rounded-xl max-h-36 object-contain border border-slate-200" />
            )}
          </div>
        </div>

        {/* Result banner */}
        {result && (
          <div className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium ${result.success ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
            {result.success ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertCircle className="h-4 w-4 shrink-0" />}
            {result.detail}
          </div>
        )}

        {/* Send button */}
        <button
          onClick={handleSend}
          disabled={!canSend}
          className="btn-glass btn-glass-pill btn-glass-blue flex items-center gap-2 disabled:opacity-20"
        >
          <Send className="h-4 w-4" />
          {sending ? 'Sending…' : recipientType === 'parent_email' ? 'Send Email to Parent' : 'Send Push Notification'}
        </button>
      </div>

      {/* Sent history with side panel */}
      <div className={`flex gap-4 ${selectedNotification ? '' : ''}`}>
        {/* Left: Sent History List */}
        <div className={`${selectedNotification ? 'w-1/2' : 'w-full'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>
            <Clock className="h-4 w-4 text-slate-400" /> Sent History
          </h3>
          {historyLoading ? (
            <p className="text-sm text-slate-400 text-center py-8">Loading…</p>
          ) : sentHistory.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No notifications sent yet.</p>
          ) : (
            <div className="space-y-1">
              {sentHistory.map(n => (
                <div
                  key={n.id}
                  onClick={() => setSelectedNotification(n)}
                  className={`py-3 px-3 rounded-xl cursor-pointer transition border ${selectedNotification?.id === n.id ? (dm ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200') : (dm ? 'border-slate-700 hover:bg-slate-700/50' : 'border-slate-100 hover:bg-slate-50')}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {n.sentVia === 'push'
                        ? <Smartphone className="h-4 w-4 text-blue-500 shrink-0" />
                        : <Mail className="h-4 w-4 text-emerald-500 shrink-0" />
                      }
                      <div className="min-w-0 flex-1">
                        <p className={`text-sm font-semibold truncate ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{n.title}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {n.recipient && (
                            <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>To: {n.recipient.name}</p>
                          )}
                          <span className={`text-xs ${dm ? 'text-slate-600' : 'text-slate-500'}`}>·</span>
                          <p className={`text-xs ${dm ? 'text-slate-600' : 'text-slate-500'}`}>{timeAgo(n.createdAt)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className={`mt-1.5 text-xs line-clamp-1 pl-6 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{n.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Vertical separator */}
        {selectedNotification && (
          <div className={`w-px ${dm ? 'bg-slate-700' : 'bg-slate-200'}`} />
        )}

        {/* Right: Email-like detail panel */}
        {selectedNotification && (
          <div className={`w-1/2 flex flex-col h-fit overflow-hidden mt-8 rounded-2xl border shadow-sm ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
            {/* Email Header */}
            <div className={`px-5 py-4 border-b ${dm ? 'border-slate-700 bg-slate-800/50' : 'border-slate-100 bg-slate-50/50'}`}>
              <div className="flex items-start justify-between mb-3">
                <h3 className={`text-lg font-semibold leading-tight pr-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>
                  {selectedNotification.title}
                </h3>
                <button
                  onClick={() => setSelectedNotification(null)}
                  className={`flex-shrink-0 p-2 rounded-xl transition ${dm ? 'text-slate-400 hover:text-slate-100 hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                  style={{ border: 'none', background: 'none', cursor: 'pointer' }}
                  title="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* Sender/Recipient row */}
              <div className="flex items-center gap-2 text-sm">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${dm ? 'bg-blue-600' : 'bg-blue-500'} text-white text-xs font-semibold`}>
                  T
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium ${dm ? 'text-slate-200' : 'text-slate-800'}`}>Teacher (You)</p>
                  <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-500'}`}>
                    to {selectedNotification.recipient?.name} &lt;{selectedNotification.recipient?.email}&gt;
                  </p>
                </div>
                <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>
                  {new Date(selectedNotification.createdAt).toLocaleString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    hour: 'numeric', 
                    minute: '2-digit'
                  })}
                </span>
              </div>
            </div>

            {/* Email Body */}
            <div className={`flex-1 p-5 min-h-[200px] ${dm ? 'bg-slate-800' : 'bg-white'}`}>
              <p className={`whitespace-pre-wrap leading-relaxed ${dm ? 'text-slate-300' : 'text-slate-700'}`}>
                {selectedNotification.message}
              </p>
            </div>

            {/* Email Footer */}
            <div className={`px-5 py-3 border-t text-xs ${dm ? 'border-slate-700 bg-slate-800/50 text-slate-500' : 'border-slate-100 bg-slate-50/50 text-slate-500'}`}>
              <div className="flex items-center gap-2">
                {selectedNotification.sentVia === 'push' ? (
                  <><Smartphone className="h-3.5 w-3.5" /> Sent via Push Notification</>
                ) : selectedNotification.sentVia === 'email' ? (
                  <><Mail className="h-3.5 w-3.5" /> Sent via Email</>
                ) : (
                  <><Info className="h-3.5 w-3.5" /> Sent via In-App</>
                )}
                <span className="mx-1">·</span>
                <span>Delivered successfully</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Notifications component ──────────────────────────────────────────────

export function Notifications() {
  const { dm } = useTeacherTheme();
  const [tab, setTab] = useState<'inbox' | 'compose'>('inbox');

  return (
    <div className="max-w-6xl mx-auto space-y-4">

      {/* Header + tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className={`text-xl font-bold ${dm ? 'text-slate-100' : 'text-slate-800'}`}>Notifications</h2>
        <div className="flex gap-1" data-dm={dm}>
          <button
            onClick={() => setTab('inbox')}
            className={`btn-glass btn-glass-pill flex items-center gap-2 ${tab === 'inbox' ? 'btn-glass-blue' : ''}`}
          >
            <Inbox className="h-4 w-4" /> Inbox
          </button>
          <button
            onClick={() => setTab('compose')}
            className={`btn-glass btn-glass-pill flex items-center gap-2 ${tab === 'compose' ? 'btn-glass-blue' : ''}`}
          >
            <Send className="h-4 w-4" /> Compose
          </button>
        </div>
      </div>

      {tab === 'inbox' ? <InboxTab dm={dm} /> : <ComposeTab dm={dm} />}
    </div>
  );
}
